import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Consultant } from "@/pages/buyer/BuyerDashboard";

const translations = {
  en: {
    title: "Add Consultant",
    subtitle: "Add a professional advisor to your team",
    fullName: "Full Name",
    company: "Company",
    specialty: "Specialty",
    specialtyPlaceholder: "e.g., M&A Attorney, CPA, Business Broker",
    email: "Email",
    phone: "Phone",
    hourlyRate: "Hourly Rate (optional)",
    bio: "Bio/Notes (optional)",
    cancel: "Cancel",
    add: "Add Consultant"
  },
  fr: {
    title: "Ajouter un Consultant",
    subtitle: "Ajoutez un conseiller professionnel à votre équipe",
    fullName: "Nom complet",
    company: "Entreprise",
    specialty: "Spécialité",
    specialtyPlaceholder: "ex: Avocat M&A, CPA, Courtier d'affaires",
    email: "Email",
    phone: "Téléphone",
    hourlyRate: "Tarif horaire (optionnel)",
    bio: "Bio/Notes (optionnel)",
    cancel: "Annuler",
    add: "Ajouter le Consultant"
  }
};

interface AddConsultantModalProps {
  onClose: () => void;
  onAdd: (data: Partial<Consultant>) => void;
  lang: 'en' | 'fr';
}

export function AddConsultantModal({ onClose, onAdd, lang }: AddConsultantModalProps) {
  const t = translations[lang];
  
  const [formData, setFormData] = useState({
    name: "",
    company: "",
    specialty: "",
    email: "",
    phone: "",
    hourlyRate: 0,
    currency: "USD",
    bio: ""
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onAdd(formData);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <CardHeader>
          <CardTitle>{t.title}</CardTitle>
          <CardDescription>{t.subtitle}</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="consultantName">{t.fullName} *</Label>
                <Input
                  id="consultantName"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  required
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="consultantCompany">{t.company}</Label>
                <Input
                  id="consultantCompany"
                  value={formData.company}
                  onChange={(e) => setFormData({...formData, company: e.target.value})}
                />
              </div>

              <div className="space-y-2 col-span-2">
                <Label htmlFor="consultantSpecialty">{t.specialty} *</Label>
                <Input
                  id="consultantSpecialty"
                  value={formData.specialty}
                  onChange={(e) => setFormData({...formData, specialty: e.target.value})}
                  placeholder={t.specialtyPlaceholder}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="consultantEmail">{t.email} *</Label>
                <Input
                  id="consultantEmail"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="consultantPhone">{t.phone}</Label>
                <Input
                  id="consultantPhone"
                  value={formData.phone}
                  onChange={(e) => setFormData({...formData, phone: e.target.value})}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="consultantRate">{t.hourlyRate}</Label>
                <Input
                  id="consultantRate"
                  type="number"
                  value={formData.hourlyRate || ""}
                  onChange={(e) => setFormData({...formData, hourlyRate: Number(e.target.value)})}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="consultantBio">{t.bio}</Label>
              <Textarea
                id="consultantBio"
                value={formData.bio}
                onChange={(e) => setFormData({...formData, bio: e.target.value})}
                rows={3}
              />
            </div>

            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={onClose}>
                {t.cancel}
              </Button>
              <Button type="submit">
                {t.add}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
